/** Build history, plus changing a live game through one composed revision: a live composed game changes from a
 * creator's request; a live generic game rebuilds with the composed builder. A generic game that never went live
 * stays retired. */
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { ComposedRevisionViewV1, Creation, CreationVersion, PlatformSettings, WorkshopState } from '@djgg/creations';
import { emptyWorkshop, workshopSelectionStatus } from '@djgg/creations';
import { GENERIC_CREATION_RETIRED, isComposedPipeline } from '@djgg/creations';
import { useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { useNow } from '../hooks/useNow.js';
import { useAttributedAction } from '../hooks/useAttributedAction.js';
import { reduceBuildStatus } from './buildStatus.js';
import { DeejWorkshop } from './DeejWorkshop.js';
import { ComposedRevisionPanel, type PendingWorkshopSelection } from './ComposedRevisionPanel.js';
import { IterationHistory } from './IterationHistory.js';

export function IteratePanel({ creation, creationFilmsEnabled = true }: {
  creation: Creation & { id: string };
  creationExperience?: PlatformSettings['creationExperience'];
  creationFilmsEnabled?: boolean;
}) {
  const services = useServices();
  const { runAttributed, attributionModal } = useAttributedAction();
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [changeOpen, setChangeOpen] = useState(true);
  const [revisionRun, setRevisionRun] = useState<ComposedRevisionViewV1 | null>(null);
  const [workshopState, setWorkshopState] = useState<WorkshopState>(emptyWorkshop);
  const [workshopOrigin, setWorkshopOrigin] = useState<number>();
  const latest = useSubscription<(CreationVersion & { id: string }) | null>(
    cb => creation.latestVersion > 0 ? services.subscribe.version(creation.id, creation.latestVersion, cb) : () => {},
    null, [creation.id, creation.latestVersion]);
  const active = useSubscription<(CreationVersion & { id: string }) | null>(
    cb => creation.activeVersion > 0 ? services.subscribe.version(creation.id, creation.activeVersion, cb) : () => {},
    null, [creation.id, creation.activeVersion]);
  const history = useSubscription<Array<CreationVersion & { id: string }>>(
    cb => services.subscribe.versions(creation.id, cb), [], [creation.id]);
  const view = useMemo(() => latest ? reduceBuildStatus(latest) : null, [latest]);
  const building = creation.buildState === 'building';
  const now = useNow(building ? 1000 : 0);
  useEffect(() => {
    let cancelled = false;
    void services.callable.workshop({ creationId: creation.id, action: 'read' }).then(result => {
      if (!cancelled) { setWorkshopState(result.state); setWorkshopOrigin(result.originVersion); }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [creation.id, creation.latestVersion, services]);
  const pendingSelections: PendingWorkshopSelection[] = workshopState.selections
    .filter(selection => selection.originVersion === workshopOrigin
      && workshopSelectionStatus(selection, active, creation.assets ?? []) !== 'applied')
    .map(({ slot, variantId }) => ({ slot, variantId }));
  // The browser-safe version view intentionally omits executionPolicy. The
  // released composition recipes are the only public pipeline snapshots with
  // these revisions plus a closed assembly identity; every callable repeats
  // the authoritative server-side composition check before writes. The game's
  // profile is its live version's, or its only (unpublished) build's.
  const profileVersion = creation.activeVersion > 0 ? active : creation.latestVersion > 0 ? latest : null;
  const profileLoaded = creation.activeVersion > 0 || creation.latestVersion > 0 ? profileVersion !== null : true;
  const composed = isComposedPipeline(profileVersion?.pipelineSnapshot);
  // A fresh Create's first build belongs to the Create page: one film from the
  // idea to the game, the automatic repair and the last-resort screen.
  const freshCreate = creation.activeVersion === 0 && creation.latestVersion > 0 && isComposedPipeline(latest?.pipelineSnapshot);
  async function cancel() {
    setCancelling(true);
    setError('');
    try {
      await runAttributed(creation, attributeTo => services.callable.cancelBuild({
        creationId: creation.id, version: creation.latestVersion, ...(attributeTo ? { attributeTo } : {}),
      }).then(() => undefined));
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not cancel the build.'); }
    finally { setCancelling(false); }
  }
  if (freshCreate) return <Navigate to={`/studio/create/${creation.id}`} replace />;
  return <div className="panel iterate-panel" data-testid="iterate-panel">
    {attributionModal}

    {profileLoaded && !composed && creation.activeVersion === 0 && <>
      <p role="status">{GENERIC_CREATION_RETIRED}</p>
      <Link className="btn" to="/studio?new=1">Start a supported game</Link>
    </>}
    {!preparing && <DeejWorkshop creation={creation} latest={latest} active={active} view={view} now={now} history={history}
      creationFilmsEnabled={creationFilmsEnabled}
      onMakeChange={() => { setChangeOpen(true); window.setTimeout(() => document.getElementById('make-a-change')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0); }} iteration={revisionRun}
      onCancel={building ? () => void cancel() : undefined} cancelling={cancelling} />}
    {profileVersion && creation.activeVersion > 0 && <div id="make-a-change" hidden={!changeOpen && !preparing}>
      <ComposedRevisionPanel key={creation.id} creation={creation}
        creationFilmsEnabled={creationFilmsEnabled}
        mode={composed ? 'change' : 'rebuild'} latest={latest} pendingSelections={pendingSelections}
        onRunChange={next => { setRevisionRun(next); if (next && next.status !== 'accepted') setChangeOpen(true); }}
        onPreparingChange={setPreparing} />
    </div>}
    {error && <p role="alert">{error}</p>}
    <details className="iterate-panel__history"><summary>Version history</summary><IterationHistory creation={creation} /></details>
  </div>;
}
